import Api from "../services/api";
import { IFeatureFlagsService } from "../services/featureFlags";
import HosProcess from "../services/hosProcess";
import { DriveStore } from "../store/reducers";
import { SecurityService } from "../utils/security";
import Translator from "../utils/translator";
import { UserService } from "../utils/users";
import { IHashMap } from "./sync.types";
import { SyncLogger } from "./logger";
import Context from "../app.context";
import { CancelablePromise } from "../utils/cancelablePromise";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { getServerDate } from "../utils/storage/date";
import { error as logError, Source } from "../utils/logger";
import { isNetworkError, isOverLimitException } from "../utils/errorHandler/error.types";
import { handleApi } from "../utils/error";
import { SECONDS } from "../constants";
import { IApiError } from "../utils/api.interfaces";

export type TSyncSource = "scheduler" | "websocket" | "user";

export const MIN_VERSION = "0000000000000000";


export const NumberOfSyncRetries = 5;

export interface IFeedResult<T> {
    data: T[];
    toVersion: string;
}

export type TSyncMethod = "Get" | "GetFeed";

export interface ISyncAddSet<T> {
    typeName: string;
    entity: T;
}

export enum RunResult {
    Success,
    Failure,
    OverLimitException
}

export interface IRunState {
    currentRunStart: Date | undefined;
    lastRunResult: RunResult;
    lastRunTimestamp: Date;
}

export interface ISyncOptions {
    api: Api;
    hosProcess: HosProcess;
    translator: Translator;
    securityService: SecurityService;
    userService: UserService;
    featureFlagsService: IFeatureFlagsService;
    store: DriveStore;
    debugFail?: boolean;
    getCachedContext: (userName: string) => Context | undefined;
}

export default class BaseSync {
    protected readonly api: Api;
    protected readonly debugFail?: boolean;
    protected readonly hosProcess: HosProcess;
    protected readonly securityService: SecurityService;
    protected readonly store: DriveStore;
    protected readonly translator: Translator;
    protected readonly userService: UserService;
    protected readonly featureFlagsService: IFeatureFlagsService;
    protected readonly syncLogger: SyncLogger;
    protected readonly getCachedContext: (userName: string) => Context | undefined;

    protected readonly runState: IRunState = {
        currentRunStart: undefined,
        lastRunResult: RunResult.Success,
        lastRunTimestamp: new Date(0)
    };

    protected currentUser: IUser | null;

    constructor(options: ISyncOptions) {
        this.api = options.api;
        this.debugFail = options.debugFail;
        this.hosProcess = options.hosProcess;
        this.securityService = options.securityService;
        this.store = options.store;
        this.translator = options.translator;
        this.userService = options.userService;
        this.featureFlagsService = options.featureFlagsService;
        this.getCachedContext = options.getCachedContext;

        this.currentUser = this.api ? this.api.getUser() : null;
        this.syncLogger = new SyncLogger();
    }

    /**
     * Clean up the objects that were just synced with the server
     * @param calls Array of calls that were synced with the server
     * @param results The results array (usually an array of ids)
     * @param getLocalEntity A method to get the entity from the sync's local dictionary
     * @param action A custom operation specific to the sync calling this method (optional)
     */
    resolveSyncedEntities<T extends IHashMap<T>> (
        calls: [string, ISyncAddSet<T>][],
        results: (string | undefined)[],
        getLocalEntity: (entity: T, idFromServer: string | undefined, index: number) => T | undefined,
        action?: (entity: T) => void
    ) {
        results.forEach((idFromServer, index) => {
            let entity = calls[index][1].entity;
            let localEntity = getLocalEntity(entity, idFromServer, index);
            if (localEntity) {
                action && action(localEntity);
                localEntity._syncCounter = NumberOfSyncRetries;
                delete localEntity.modified;
                delete localEntity.previousState;
            }
        });
    }

    /**
     * Sanitize entity to be synced with the server by removing client side properties. Client side properties usually start with an underscore.
     * @param sanitizedEntity The entity to be sanitized
     */
    removeClientSideProperties<T extends IHashMap<T>>(sanitizedEntity: T) {
        delete sanitizedEntity.modified;
        delete sanitizedEntity.previousState;
        delete sanitizedEntity.fromServer;

        Object.keys(sanitizedEntity).forEach(property => {
            if (property.charAt(0) === "_") {
                delete sanitizedEntity[property];
            }
        });
    }

    createProfiledSync<T>(measureName: MeasureName, syncFn: () => CancelablePromise<T>, profilingBatcher?: PerformanceProfileBatcher): CancelablePromise<T> {
        const profiler = PerformanceProfilerFactory.getInstance(measureName, profilingBatcher);
        return profiler.measureCancelable(syncFn);
    }

    didSyncFailRecently() {
        return this.runState.lastRunResult !== RunResult.Success && ((getServerDate().getTime() - this.runState.lastRunTimestamp.getTime()) < 30 * SECONDS);
    }

    verifyIfPreviousSyncWasCompleted(syncType: string) {
        const currentDate = getServerDate();
        if (this.runState.currentRunStart !== undefined) {
            logError(
                Source.SYNCERROR,
                `Current ${ syncType } sync started at ${ currentDate.toISOString() }.` +
                ` Last ${ syncType } sync was started at ${ this.runState.currentRunStart.toISOString() }, but it isn't complete yet.`,
                this.currentUser
            );
        }
        this.runState.currentRunStart = currentDate;
    }

    updateRunStateAtSyncComplete(error?) {
        this.runState.currentRunStart && (this.runState.currentRunStart = undefined);

        if (error) {
            if (!isNetworkError(error)) {
                this.runState.lastRunTimestamp = getServerDate();
                this.runState.lastRunResult = isOverLimitException(error) ? RunResult.OverLimitException : RunResult.Failure;
            }
            handleApi(error, true);
        } else {
            this.runState.lastRunTimestamp = getServerDate();
            this.runState.lastRunResult = RunResult.Success;
        }
    }

    async createProfiledSyncAsync<T>(measureName: MeasureName, syncFn: () => Promise<T>, profilingBatcher?: PerformanceProfileBatcher): Promise<T> {
        const profiler = PerformanceProfilerFactory.getInstance(measureName, profilingBatcher);
        return profiler.measureAsync(syncFn);
    }

    /**
     * Extracts the highest version from an array of entities
     * @param data - Array of entities with version property
     * @returns The highest version string, or MIN_VERSION if array is empty
     */
    protected getLatestVersion<TInterface extends { version?: string }>(data: TInterface[]): string {
        if (!data.length) {
            return MIN_VERSION;
        }

        // Find the highest version by comparing all versions
        const maxVersion = data.reduce((max, item) => {
            const version = item.version ?? "";
            return version > max ? version : max;
        }, "");

        return maxVersion || MIN_VERSION;
    }

    /**
     * Handles partial MultiCall success by resolving successful calls before processing the error.
     * This prevents successful operations from being retried when the error handler retries.
     *
     * @param errorObject - Error from multiCall with requestIndex
     * @param calls - Array of calls that were sent to multiCall
     * @param getLocalEntity - Function to get the local entity for resolving
     */
    protected handlePartialMultiCallSuccess<T extends IHashMap<T>>(
        errorObject: IApiError,
        calls: [string, ISyncAddSet<T>][],
        getLocalEntity: (entity: T) => T | undefined
    ): void {
        const requestIndex = errorObject?.data?.requestIndex;

        if (requestIndex !== undefined && requestIndex > 0) {
            for (let i = 0; i < requestIndex; i++) {
                const entity = calls[i][1].entity;
                const localEntity = getLocalEntity(entity);
                if (localEntity) {
                    localEntity._syncCounter = NumberOfSyncRetries;
                    delete localEntity.modified;
                    delete localEntity.previousState;
                }
            }
        }
    }
}
